import React, { useEffect, useState } from "react";
import { Loader2, ShieldCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { fmtDate } from "./ui";

const STALE_DAYS = 7;
const AUTO_SYNC_FLAG = "rfmg_auto_sync_fired";

/**
 * Self-contained Drive Sync Health badge.
 * Fetches /admin/media/sync/status, renders one of:
 *   ALL GREEN · CONFIGURED · NO SYNC YET · LAST RUN FAILED · AT RISK · SYNCING…
 * Auto-fires Sync Now once per browser session if last sync is stale (>7d) or missing.
 *
 * Use in dashboards or any admin surface where Drive health matters at a glance.
 */
const SyncHealthBadge = () => {
  const [sync, setSync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [autoSyncHint, setAutoSyncHint] = useState(null);

  const fetchStatus = async () => {
    try {
      const r = await api.get("/admin/media/sync/status");
      setSync(r.data);
      return r.data;
    } catch {
      return null;
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  // Auto-heal: once per session, if configured + stale, fire Sync Now.
  useEffect(() => {
    if (!sync || !sync.configured || syncing) return;
    if (sessionStorage.getItem(AUTO_SYNC_FLAG) === "1") return;

    const last = sync.last_sync;
    const lastWhen = last?.finished_at || last?.started_at || null;
    const lastStatus = (last?.status || "").toLowerCase();
    const ageDays = lastWhen ? (Date.now() - new Date(lastWhen).getTime()) / 86400000 : Infinity;
    const stale = !last || lastStatus === "error" || ageDays > STALE_DAYS;
    if (!stale) return;

    sessionStorage.setItem(AUTO_SYNC_FLAG, "1");
    const reason = !last ? "no prior sync" : lastStatus === "error" ? "last run failed" : `last sync ${Math.round(ageDays)} days ago`;
    setAutoSyncHint(`Auto-syncing Drive (${reason})…`);
    (async () => {
      setSyncing(true);
      try {
        await api.post("/admin/media/sync/run");
        toast.success("Drive auto-synced — recordings refreshed.");
        await fetchStatus();
      } catch {
        toast.error("Auto-sync failed — open Media Library and try Sync Now.");
      } finally {
        setSyncing(false);
        setAutoSyncHint(null);
      }
    })();
  }, [sync, syncing]);

  if (!sync) return null;

  // Syncing state (auto-heal in progress) takes visual precedence
  if (autoSyncHint || syncing) {
    const tone = { border: "#F28C18", bg: "rgba(242,140,24,0.10)", text: "#F28C18" };
    return (
      <div
        className="bento-card !p-4 flex items-start gap-3"
        style={{ borderColor: tone.border + "66", background: tone.bg }}
        data-testid="sync-health-badge"
      >
        <Loader2 className="h-5 w-5 mt-0.5 flex-shrink-0 animate-spin" style={{ color: tone.text }} />
        <div className="text-sm text-[#F6F0E6] min-w-0">
          <div className="font-medium tracking-wide" style={{ color: tone.text }} data-testid="sync-health-headline">
            Drive Sync Health: SYNCING…
          </div>
          <div className="text-[#D9D9D9]/85 mt-1 text-[12.5px]">
            {autoSyncHint || "Pulling the latest sermons from Drive — this usually takes 5–15 seconds."}
          </div>
        </div>
      </div>
    );
  }

  const last = sync.last_sync || null;
  const lastStatus = (last?.status || "").toLowerCase();
  const lastWhen = last?.finished_at || last?.started_at || null;

  let tone, Icon, headline, detail;
  if (!sync.configured) {
    tone = { border: "#E8452C", bg: "rgba(232,69,44,0.08)", text: "#E8452C" };
    Icon = ShieldAlert;
    headline = "Drive Sync Health: AT RISK";
    detail = `Missing env: ${sync.missing_env.join(", ") || "credentials"}. Recordings will disappear on next deploy until added to Emergent Deploy → Environment Variables.`;
  } else if (lastStatus === "error" || lastStatus === "not_configured") {
    tone = { border: "#E8452C", bg: "rgba(232,69,44,0.08)", text: "#E8452C" };
    Icon = ShieldAlert;
    headline = "Drive Sync Health: LAST RUN FAILED";
    detail = `Last attempt: ${lastStatus}${lastWhen ? ` · ${fmtDate(lastWhen)}` : ""}. Open Media Library and click "Sync Now" to retry.`;
  } else if (!last) {
    tone = { border: "#F28C18", bg: "rgba(242,140,24,0.08)", text: "#F28C18" };
    Icon = ShieldCheck;
    headline = "Drive Sync Health: CONFIGURED · NO SYNC YET";
    detail = `Source: ${sync.source_account}. Open Media Library and click "Sync Now" to import existing sermons.`;
  } else {
    tone = { border: "#C9A44C", bg: "rgba(201,164,76,0.06)", text: "#C9A44C" };
    Icon = ShieldCheck;
    headline = "Drive Sync Health: ALL GREEN";
    detail = `Source: ${sync.source_account}${lastWhen ? ` · Last sync ${fmtDate(lastWhen)}` : ""}${typeof last?.files_imported === "number" ? ` · ${last.files_imported} imported, ${last.files_updated || 0} updated` : ""}.`;
  }

  return (
    <div
      className="bento-card !p-4 flex items-start gap-3"
      style={{ borderColor: tone.border + "66", background: tone.bg }}
      data-testid="sync-health-badge"
    >
      <Icon className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: tone.text }} />
      <div className="text-sm text-[#F6F0E6] min-w-0">
        <div className="font-medium tracking-wide" style={{ color: tone.text }} data-testid="sync-health-headline">
          {headline}
        </div>
        <div className="text-[#D9D9D9]/85 mt-1 text-[12.5px]">{detail}</div>
      </div>
    </div>
  );
};

export default SyncHealthBadge;
